import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { ChevronLeft, ChevronRight, Plus, Trash2, Edit2, List, MapPin, Calendar, Clock ,UserCog } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import TextField from '../common/TextField';
import CustomDatePicker from '../common/CustomDatePicker';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../common/Toast';
import { db } from '../../firebase/config';
import { getCoaches, bulkDeleteAssignments } from '../../firebase/firestore';
import PickupAPI from '../../firebase/apis/api-pickup.js';

/* ─────────────────────────── constants ─────────────────────────── */

const ASSIGNMENTS_COLLECTION = 'coachAssignments';
const ROLE_OPTIONS = ['Sports Coach', 'Cheer Coach', 'Site Manager'];

// Calendar display range (7 am – 10 pm)
const CAL_START_H = 7;
const CAL_END_H = 22;
const SLOT_H = 24; // px per 30-min slot
const TOTAL_SLOTS = (CAL_END_H - CAL_START_H) * 2;
const TOTAL_CAL_H = TOTAL_SLOTS * SLOT_H;

/* ─────────────────────────── helpers ───────────────────────────── */

function isApprovedCoach(coach) {
  const status = String(coach?.approvalStatus || coach?.status || '').toLowerCase();
  if (status === 'pending' || status === 'rejected') return false;
  if (coach?.isActive === false) return false;
  return (
    coach?.role === 'coach' ||
    coach?.role === 'Coach' ||
    status === 'approved' ||
    coach?.approvalStatus === 'approved'
  );
}

function formatCoachName(coach) {
  const name = `${coach?.firstName || ''} ${coach?.lastName || ''}`.trim();
  return name || coach?.email || 'Unnamed coach';
}

function initialsFromName(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || 'C';
}

function parseMmDdYyyy(dateStr) {
  const m = String(dateStr || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLongDate(dateStr) {
  const d = parseMmDdYyyy(dateStr);
  if (!d) return dateStr || '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTime12h(hhmm) {
  const m = String(hhmm || '').match(/^(\d{2}):(\d{2})$/);
  if (!m) return hhmm || '—';
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

function getCoachDisplayName(row) {
  // Handle multi-coach assignments
  if (row.coachIds && Array.isArray(row.coachIds) && row.coachNames) {
    return row.coachNames.join(', ');
  } else if (row.coachIds && Array.isArray(row.coachIds)) {
    // Fallback: show count if names not available
    return `${row.coachIds.length} coach${row.coachIds.length !== 1 ? 'es' : ''}`;
  }
  // Legacy single-coach assignments
  return row.coachName || '—';
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfWeekSunday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function combineDateAndTime(reportDateStr, hhmm) {
  const d = parseMmDdYyyy(reportDateStr);
  const m = String(hhmm || '').match(/^(\d{2}):(\d{2})$/);
  if (!d || !m) return null;
  const dt = new Date(d);
  dt.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return dt;
}

function getAssignmentStartEndLocal(a) {
  const startFromFields = combineDateAndTime(a?.reportDate, a?.startTime);
  const endFromFields = combineDateAndTime(a?.reportDate, a?.endTime);
  if (startFromFields && endFromFields) return { start: startFromFields, end: endFromFields };
  const startTs = a?.startDateTime?.toDate
    ? a.startDateTime.toDate()
    : a?.startDateTime
    ? new Date(a.startDateTime)
    : null;
  const endTs = a?.endDateTime?.toDate
    ? a.endDateTime.toDate()
    : a?.endDateTime
    ? new Date(a.endDateTime)
    : null;
  return { start: startTs, end: endTs };
}

function normalizeLocation(loc) {
  if (typeof loc === 'string') return { id: loc, name: loc, address: '' };
  const id = loc?.id || loc?._id || loc?.docId || loc?.name || JSON.stringify(loc);
  const name = loc?.name || loc?.siteName || loc?.schoolName || loc?.title || 'Unknown Site';
  const address =
    loc?.address ||
    loc?.location ||
    [loc?.street, loc?.city, loc?.state, loc?.zip].filter(Boolean).join(', ') ||
    '';
  return { id, name, address };
}

/** Match assignment coachId to list row even if stored id used uid vs document id. */
function getCoachIdVariants(c) {
  if (!c) return [];
  return [c.id, c._id, c.userId, c.uid].filter((x) => x != null && x !== '').map(String);
}

function findCoachInList(rawCoachId, coachList) {
  if (rawCoachId == null || rawCoachId === '') return null;
  const want = String(rawCoachId);
  return coachList.find((c) => getCoachIdVariants(c).includes(want)) || null;
}

function coachPrimaryId(c) {
  if (!c) return '';
  const v = c.id ?? c._id;
  return v != null ? String(v) : '';
}

/** Resolve site by id first, then by saved site name (handles id format drift). */
function findLocationInList(rawSiteId, siteName, pickupSchoolList) {
  if (rawSiteId != null && rawSiteId !== '') {
    const byId = pickupSchoolList.find((l) => String(l.id) === String(rawSiteId));
    if (byId) return byId;
  }
  if (siteName) {
    const norm = String(siteName).trim().toLowerCase();
    return (
      pickupSchoolList.find((l) => String(l.name || '').trim().toLowerCase() === norm) || null
    );
  }
  return null;
}

/** Build form state for edit/create from a saved assignment row + current lists. */
function buildFormDataFromAssignmentRow(row, coachList, pickupSites) {
  const resolvedCoach = findCoachInList(row?.coachId, coachList);
  const resolvedLoc = findLocationInList(row?.siteId, row?.siteName, pickupSites);
  const coachIdForForm = resolvedCoach
    ? coachPrimaryId(resolvedCoach)
    : String(row?.coachId || '');
  const siteIdForForm = resolvedLoc ? String(resolvedLoc.id) : String(row?.siteId || '');
  
  // Handle multi-coach assignments
  let coachIds = [];
  let coachNames = [];
  
  if (row?.coachIds && Array.isArray(row.coachIds)) {
    // Multi-coach assignment: use the coachIds array
    coachIds = row.coachIds.map(id => String(id));
    coachNames = row.coachNames || [];
    
    // If coachNames is missing or incomplete, resolve from coachList
    if (coachNames.length !== coachIds.length) {
      coachNames = coachIds.map(coachId => {
        const coach = coachList.find(c => String(c.id) === coachId);
        return coach ? formatCoachName(coach) : '';
      });
    }
  } else if (row?.coachId) {
    // Legacy single-coach assignment: convert to multi-coach format
    coachIds = [coachIdForForm];
    coachNames = [resolvedCoach ? formatCoachName(resolvedCoach) : row?.coachName || ''];
  }
  
  return {
    // Multi-coach support
    coachIds,
    coachNames,
    // Legacy single-coach support (for backward compatibility)
    coachId: coachIds.length === 1 ? coachIds[0] : '',
    coachName: coachNames.length === 1 ? coachNames[0] : '',
    // Other fields
    siteId: siteIdForForm,
    siteName: resolvedLoc ? resolvedLoc.name || row?.siteName || '' : row?.siteName || '',
    address: row?.address || '',
    reportDate: row?.reportDate || '',
    startTime: row?.startTime || '14:00',
    endTime: row?.endTime || '16:00',
    role: row?.role || ROLE_OPTIONS[0],
    notes: row?.notes || '',
  };
}

function weekLabel(weekStart) {
  const end = addDays(weekStart, 6);
  const s = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Week of ${s} – ${e}`;
}

/* ─────────────────────────── role colours ──────────────────────── */

const ROLE_STYLES = {
'Sports Coach': {
    avatar: 'bg-gray-100 text-gray-900',
    badge: 'bg-gray-100 text-gray-800',
    event: 'bg-gray-100 border-l-4 border-gray-500 text-gray-900',
    dot: 'bg-gray-500',
  },
  'Cheer Coach': {
    avatar: 'bg-pink-100 text-pink-900',
    badge: 'bg-pink-100 text-pink-800',
    event: 'bg-pink-100 border-l-4 border-pink-500 text-pink-900',
    dot: 'bg-pink-500',
  },
  'Site Manager': {
    avatar: 'bg-green-100 text-green-900',
    badge: 'bg-green-100 text-green-800',
    event: 'bg-green-100 border-l-4 border-green-500 text-green-900',
    dot: 'bg-green-500',
  },
};

function roleStyle(role) {
  return (
    ROLE_STYLES[role] || {
      avatar: 'bg-indigo-100 text-indigo-900',
      badge: 'bg-indigo-100 text-indigo-800',
      event: 'bg-indigo-100 border-l-4 border-indigo-500 text-indigo-900',
      dot: 'bg-indigo-500',
    }
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LIST VIEW
═══════════════════════════════════════════════════════════════════ */


const AssignmentListRow = ({ row, onEdit, onDelete, isSelected, onToggleSelection }) => {
  const rs = roleStyle(row.role);

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4 hover:shadow-sm transition-all">
      
      {/* LEFT SECTION */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelection(row.id)}
          className="h-4 w-4 text-blue-600 rounded border-gray-300"
        />

        {/* Avatar */}
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center text-sm font-semibold flex-shrink-0 ${rs.avatar}`}
        >
          {initialsFromName(getCoachDisplayName(row))}
        </div>

        {/* CONTENT */}
        <div className="flex flex-col min-w-0 w-full">
          
          {/* TOP ROW → Name + Role */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[15px] font-semibold text-gray-900 truncate">
              {getCoachDisplayName(row) || "—"}
            </span>

            {/* 🔥 Role Badge with Icon */}
            <span
              className={`flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${rs.badge}`}
            >
              <UserCog size={12} className="opacity-80" />
              {row.role}
            </span>
          </div>

          {/* SECOND ROW → Site + Address + Date + Time */}
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-gray-600 mt-1">
            
            {/* Location */}
            <div className="flex items-center gap-1 min-w-0">
              <MapPin size={14} className="text-gray-400 flex-shrink-0" />
              <span className="font-medium truncate">
                {row.siteName || "—"}
              </span>
              {row.address && (
                <span className="text-gray-400 truncate hidden md:inline">
                  · {row.address}
                </span>
              )}
            </div>

            {/* Divider */}
            <span className="text-gray-300 hidden sm:inline">|</span>

            {/* Date */}
            <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-[11px]">
              <Calendar size={12} className="text-gray-500" />
              <span className="text-gray-600 whitespace-nowrap">
                {formatLongDate(row.reportDate)}
              </span>
            </div>

            {/* Time */}
            <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-[11px]">
              <Clock size={12} className="text-gray-500" />
              <span className="text-gray-600 whitespace-nowrap">
                {formatTime12h(row.startTime)} – {formatTime12h(row.endTime)}
              </span>
            </div>
          </div>

          {/* Notes */}
          {row.notes && (
            <p className="text-[12px] text-gray-400 mt-1 truncate">
              {row.notes}
            </p>
          )}
        </div>
      </div>

      {/* RIGHT ACTIONS */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onEdit(row)}
          className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition"
          title="Edit"
        >
          <Edit2 size={16} />
        </button>

        <button
          onClick={() => onDelete(row)}
          className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-red-50 text-gray-500 hover:text-red-600 transition"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};




/* ═══════════════════════════════════════════════════════════════════
   CALENDAR VIEW
═══════════════════════════════════════════════════════════════════ */

const WeeklyCalendar = ({ weekStart, assignments, onPrevWeek, onNextWeek, onEdit, onDelete }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const byDay = useMemo(() => {
    const map = new Map();
    for (const a of assignments) {
      const { start } = getAssignmentStartEndLocal(a);
      if (!start || Number.isNaN(start.getTime())) continue;
      const key = start.toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return map;
  }, [assignments]);

  const timeLabels = useMemo(() => {
    const labels = [];
    for (let slot = 0; slot < TOTAL_SLOTS; slot++) {
      const totalMin = CAL_START_H * 60 + slot * 30;
      const h = Math.floor(totalMin / 60);
      const mn = totalMin % 60;
      labels.push({ slot, isHour: mn === 0, label: formatTime12h(`${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`) });
    }
    return labels;
  }, []);

  const renderEventBlock = (a) => {
    const { start, end } = getAssignmentStartEndLocal(a);
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const sMin = start.getHours() * 60 + start.getMinutes();
    const eMin = end.getHours() * 60 + end.getMinutes();
    const clampS = Math.max(CAL_START_H * 60, Math.min(CAL_END_H * 60, sMin));
    const clampE = Math.max(CAL_START_H * 60, Math.min(CAL_END_H * 60, eMin));
    const topPx = ((clampS - CAL_START_H * 60) / 30) * SLOT_H;
    const heightPx = Math.max(26, ((clampE - clampS) / 30) * SLOT_H);
    const rs = roleStyle(a.role);

    return (
      <div
        key={a.id}
        className={`absolute left-1 right-1 rounded-md px-2 py-1 cursor-pointer hover:brightness-95 transition-all group/evt ${rs.event}`}
        style={{ top: topPx, height: heightPx, overflow: 'hidden' }}
        title={`${getCoachDisplayName(a)} · ${a.siteName} · ${formatTime12h(a.startTime)} – ${formatTime12h(a.endTime)}`}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold truncate leading-tight">{getCoachDisplayName(a)}</div>
            {heightPx > 36 && (
              <div className="text-[10px] opacity-75 truncate leading-tight">{a.siteName}</div>
            )}
            {heightPx > 52 && (
              <div className="text-[10px] opacity-60 leading-tight">
                {formatTime12h(a.startTime)} – {formatTime12h(a.endTime)}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 opacity-0 group-hover/evt:opacity-100 transition-opacity flex gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(a); }}
              className="p-0.5 rounded hover:bg-white/40"
            >
              <Edit2 size={10} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(a); }}
              className="p-0.5 rounded hover:bg-white/40"
            >
              <Trash2 size={10} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div>
          <div className="text-[15px] font-semibold text-gray-900">{weekLabel(weekStart)}</div>
          <div className="text-[12px] text-gray-500 mt-0.5">Starts Sunday</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onPrevWeek}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={15} /> Prev
          </button>
          <button
            onClick={() => {}}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Today
          </button>
          <button
            onClick={onNextWeek}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Next <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="overflow-x-auto">
        <div style={{ minWidth: 640 }}>
          {/* Day name headers */}
          <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}>
            <div className="bg-gray-50 border-r border-gray-100" />
            {days.map((d) => {
              const isToday = d.getTime() === today.getTime();
              return (
                <div key={d.toISOString()} className="bg-gray-50 border-r border-gray-100 last:border-r-0 py-2.5 px-2 text-center">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    {d.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div
                    className={`text-[14px] font-semibold mt-0.5 mx-auto w-7 h-7 flex items-center justify-center rounded-full ${
                      isToday ? 'bg-blue-600 text-white' : 'text-gray-800'
                    }`}
                  >
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Scrollable time grid */}
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}>
              {/* Time labels */}
              <div className="border-r border-gray-100 bg-white">
                {timeLabels.map(({ slot, isHour, label }) => (
                  <div
                    key={slot}
                    className="border-b border-gray-100 flex items-start justify-end pr-2 pt-1"
                    style={{ height: SLOT_H }}
                  >
                    {isHour && (
                      <span className="text-[10px] text-gray-400 leading-none">{label}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {days.map((d) => {
                const key = d.toISOString().slice(0, 10);
                const dayEvents = byDay.get(key) || [];
                return (
                  <div key={key} className="border-r border-gray-100 last:border-r-0 relative">
                    {/* Slot background lines */}
                    {timeLabels.map(({ slot, isHour }) => (
                      <div
                        key={slot}
                        className={`${isHour ? 'border-b border-gray-100' : 'border-b border-gray-50'}`}
                        style={{ height: SLOT_H }}
                      />
                    ))}
                    {/* Event blocks */}
                    <div className="absolute inset-0">{dayEvents.map(renderEventBlock)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-t border-gray-100 bg-gray-50">
        {ROLE_OPTIONS.map((r) => {
          const rs = roleStyle(r);
          return (
            <div key={r} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${rs.dot}`} />
              <span className="text-[11px] text-gray-500">{r}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   LOCATION VIEW
═══════════════════════════════════════════════════════════════════ */

const LocationView = ({ assignments, pickupSites, onEdit, onDelete }) => {
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  // Group assignments by site
  const assignmentsBySite = useMemo(() => {
    const grouped = new Map();
    assignments.forEach((assignment) => {
      const siteId = assignment.siteId || assignment.siteName || 'unknown';
      if (!grouped.has(siteId)) {
        grouped.set(siteId, {
          id: siteId,
          name: assignment.siteName || 'Unknown Site',
          address: assignment.address || '',
          assignments: [],
        });
      }
      grouped.get(siteId).assignments.push(assignment);
    });
    return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [assignments]);

  // Debug: log assignments for troubleshooting
  useEffect(() => {
    console.log('LocationView - assignments:', assignments);
    console.log('LocationView - assignmentsBySite:', assignmentsBySite);
  }, [assignments, assignmentsBySite]);

  // Get unique coaches for selected site
  const selectedSiteCoaches = useMemo(() => {
    if (!selectedSiteId) return [];
    const site = assignmentsBySite.find((s) => s.id === selectedSiteId);
    if (!site) return [];
    
    // Get unique coaches by coachId
    const coachMap = new Map();
    site.assignments.forEach((assignment) => {
      if (!coachMap.has(assignment.coachId)) {
        coachMap.set(assignment.coachId, {
          coachId: assignment.coachId,
          coachName: assignment.coachName,
          role: assignment.role,
          assignments: [],
        });
      }
      coachMap.get(assignment.coachId).assignments.push(assignment);
    });
    
    return Array.from(coachMap.values());
  }, [selectedSiteId, assignmentsBySite]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="text-[15px] font-semibold text-gray-900">Assignments by Location</div>
        <div className="text-[12px] text-gray-500 mt-0.5">Select a school to view assigned coaches</div>
      </div>

      <div className="flex">
        {/* Left sidebar - School list */}
        <div className="w-80 border-r border-gray-100 bg-gray-50">
          <div className="p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Schools ({assignmentsBySite.length})
            </div>
            <div className="space-y-1">
              {assignmentsBySite.map((site) => (
                <button
                  key={site.id}
                  onClick={() => setSelectedSiteId(site.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
                    selectedSiteId === site.id
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="truncate">{site.name}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {site.assignments.length} assignment{site.assignments.length !== 1 ? 's' : ''}
                  </div>
                </button>
              ))}
              {assignmentsBySite.length === 0 && (
                <div className="text-center py-8 text-[13px] text-gray-400">
                  No assignments found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Coach details */}
        <div className="flex-1">
          {selectedSiteId ? (
            <div className="p-5">
              {selectedSiteCoaches.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Assigned Coaches ({selectedSiteCoaches.length})
                  </div>
                  {selectedSiteCoaches.map((coach) => {
                    const rs = roleStyle(coach.role);
                    return (
                      <div
                        key={coach.coachId}
                        className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold ${rs.avatar}`}
                            >
                              {initialsFromName(coach.coachName)}
                            </div>
                            <div>
                              <div className="text-[14px] font-semibold text-gray-900">
                                {coach.coachName}
                              </div>
                              <span
                                className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${rs.badge}`}
                              >
                                <UserCog size={10} className="opacity-80" />
                                {coach.role}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {coach.assignments.map((assignment) => (
                            <div
                              key={assignment.id}
                              className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-3 py-2 text-[12px]"
                            >
                              <div className="flex items-center gap-3 text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Calendar size={12} className="text-gray-400" />
                                  <span>{formatLongDate(assignment.reportDate)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock size={12} className="text-gray-400" />
                                  <span>
                                    {formatTime12h(assignment.startTime)} – {formatTime12h(assignment.endTime)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => onEdit(assignment)}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
                                  title="Edit"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  onClick={() => onDelete(assignment)}
                                  className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600 transition"
                                  title="Delete"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-300 text-4xl mb-3">👥</div>
                  <div className="text-[14px] font-medium text-gray-500">
                    No coaches assigned to this location
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[300px]">
              <div className="text-center">
                <div className="text-gray-300 text-4xl mb-3">🏫</div>
                <div className="text-[14px] font-medium text-gray-500">
                  Select a school to view assigned coaches
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════ */

const CoachAssignments = () => {
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [pickupSites, setPickupSites] = useState([]);
  const [dataError, setDataError] = useState('');

  const [activeView, setActiveView] = useState('list'); // 'list' | 'calendar' | 'location'
  const [showExpired, setShowExpired] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [weekStart, setWeekStart] = useState(() => startOfWeekSunday(new Date()));

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Selection state for bulk operations
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
  
  // Toast notifications
  const { toasts, success, error, warning, info, removeToast } = useToast();
  const [formData, setFormData] = useState({
    coachId: '',
    coachName: '',
    siteId: '',
    siteName: '',
    address: '',
    reportDate: '',
    startTime: '14:00',
    endTime: '16:00',
    role: ROLE_OPTIONS[0],
    notes: '',
  });

  /** Re-sync edit form when coaches/locations finish loading after modal open. */
  const editResolvedRef = useRef(null);

  /* ── Firestore ── */

  useEffect(() => {
    let unsub = () => {};
    try {
      const q = query(collection(db, ASSIGNMENTS_COLLECTION), orderBy('startDateTime', 'asc'));
      unsub = onSnapshot(
        q,
        (snap) => {
          setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          console.error('onSnapshot error:', err);
          setDataError(err?.message || String(err));
          setLoading(false);
        }
      );
    } catch (err) {
      setDataError(err?.message || String(err));
      setLoading(false);
    }
    return () => unsub();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [coachesData, pickupData] = await Promise.all([getCoaches(), PickupAPI.getAllSchools()]);
        if (!mounted) return;
        const approved = (coachesData || [])
          .filter(isApprovedCoach)
          .sort((a, b) => formatCoachName(a).localeCompare(formatCoachName(b)));
        setCoaches(approved);
        setPickupSites(
          (pickupData || [])
            .map(normalizeLocation)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        if (!mounted) return;
        setDataError((prev) => prev || err?.message || String(err));
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isFormOpen || !editingAssignment?.id) {
      editResolvedRef.current = null;
      return;
    }
    const key = editingAssignment.id;
    if (editResolvedRef.current === key) return;
    if (coaches.length === 0 && pickupSites.length === 0) return;
    editResolvedRef.current = key;
    setFormData(buildFormDataFromAssignmentRow(editingAssignment, coaches, pickupSites));
  }, [isFormOpen, editingAssignment, coaches, pickupSites]);

  /* ── Filtered / sorted data ── */

  const filteredAssignments = useMemo(() => {
    const q = searchVal.trim().toLowerCase();
    const now = new Date();
    return assignments
      .filter((a) => {
        if (!showExpired) {
          const exp = a?.expiresAt?.toDate
            ? a.expiresAt.toDate()
            : a?.expiresAt
            ? new Date(a.expiresAt)
            : null;
          if (exp && !Number.isNaN(exp.getTime()) && exp < now) return false;
        }
        if (!q) return true;
        const hay = `${a.coachName} ${a.siteName} ${a.address} ${a.role} ${a.notes}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const as = getAssignmentStartEndLocal(a).start;
        const bs = getAssignmentStartEndLocal(b).start;
        return as - bs;
      });
  }, [assignments, searchVal, showExpired]);

  const weekAssignments = useMemo(() => {
    const start = new Date(weekStart);
    const end = addDays(start, 7);
    return filteredAssignments.filter((a) => {
      const s = getAssignmentStartEndLocal(a).start;
      return s && !Number.isNaN(s.getTime()) && s >= start && s < end;
    });
  }, [filteredAssignments, weekStart]);

  const deleteExpiredAssignments = async () => {
    const now = new Date();
    const expired = assignments.filter((a) => {
      const exp = a?.expiresAt?.toDate
        ? a.expiresAt.toDate()
        : a?.expiresAt ? new Date(a.expiresAt) : null;
      return exp && !Number.isNaN(exp.getTime()) && exp < now;
    });
    if (!expired.length) { warning('No expired assignments to delete.'); return; }
    if (!window.confirm(`Delete ${expired.length} expired assignment(s)?`)) return;
    try {
      for (const row of expired) {
        await deleteDoc(doc(db, ASSIGNMENTS_COLLECTION, row.id));
      }
      success(`Deleted ${expired.length} expired assignment(s).`);
    } catch (err) {
      error('Failed to delete expired assignments: ' + (err?.message || String(err)));
    }
  };

  // Selection handlers for bulk operations
  const toggleAssignmentSelection = (assignmentId) => {
    setSelectedAssignmentIds((prevSelected) =>
      prevSelected.includes(assignmentId)
        ? prevSelected.filter(id => id !== assignmentId)
        : [...prevSelected, assignmentId]
    );
  };

  const handleSelectAllOnPage = (assignmentIdsOnPage) => {
    const allSelected = assignmentIdsOnPage.every(id => selectedAssignmentIds.includes(id));
    if (allSelected) {
      setSelectedAssignmentIds(prev => prev.filter(id => !assignmentIdsOnPage.includes(id)));
    } else {
      const combined = new Set([...selectedAssignmentIds, ...assignmentIdsOnPage]);
      setSelectedAssignmentIds(Array.from(combined));
    }
  };

  const handleBulkDeleteAssignments = async () => {
    if (selectedAssignmentIds.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedAssignmentIds.length} selected assignment(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      await bulkDeleteAssignments(selectedAssignmentIds);
      setSelectedAssignmentIds([]);
      success(`Successfully deleted ${selectedAssignmentIds.length} assignment(s).`);
    } catch (error) {
      console.error('❌ Error bulk deleting assignments:', error);
      error('Error deleting selected assignments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Keep selections in sync with available data
  useEffect(() => {
    setSelectedAssignmentIds((prev) => prev.filter(id => assignments.some(assignment => assignment.id === id)));
  }, [assignments]);

  /* ── Form handlers ── */

  const openNew = () => {
    setEditingAssignment(null);
    setFormData({
      coachIds: [], coachNames: [],
      coachId: '', coachName: '', // Legacy support
      siteId: '', siteName: '', address: '',
      reportDate: '', startTime: '14:00', endTime: '16:00', role: ROLE_OPTIONS[0], notes: '',
    });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    editResolvedRef.current = null;
    setEditingAssignment(row);
    setFormData(buildFormDataFromAssignmentRow(row, coaches, pickupSites));
    setIsFormOpen(true);
  };

  const handleSiteChange = (siteId) => {
    const selected = pickupSites.find((l) => String(l.id) === String(siteId));
    setFormData((prev) => {
      const prevSiteAddress = pickupSites.find((l) => String(l.id) === String(prev.siteId))?.address;
      const shouldAutofill = !prev.address || prev.address === prevSiteAddress;
      return {
        ...prev,
        siteId,
        siteName: selected?.name || '',
        address: shouldAutofill ? selected?.address || '' : prev.address,
      };
    });
  };

  const handleCoachChange = (coachId) => {
    const c = coaches.find((x) => String(x.id) === String(coachId));
    setFormData((prev) => ({ ...prev, coachId, coachName: c ? formatCoachName(c) : '' }));
  };

  const handleMultiCoachChange = (selectedCoachIds) => {
    const selectedCoaches = coaches.filter((c) => selectedCoachIds.includes(String(c.id)));
    const selectedCoachNames = selectedCoaches.map((c) => formatCoachName(c));
    setFormData((prev) => ({ 
      ...prev, 
      coachIds: selectedCoachIds, 
      coachNames: selectedCoachNames,
      // Update legacy fields for backward compatibility
      coachId: selectedCoachIds.length === 1 ? selectedCoachIds[0] : '',
      coachName: selectedCoachNames.length === 1 ? selectedCoachNames[0] : ''
    }));
  };

  const validateForm = () => {
    // Support both multi-coach and single coach validation
    const hasMultiCoaches = formData.coachIds && formData.coachIds.length > 0;
    const hasSingleCoach = formData.coachId;
    
    if (!hasMultiCoaches && !hasSingleCoach) return 'Please select at least one coach.';
    if (!formData.siteId) return 'Please select a site.';
    if (!formData.reportDate) return 'Please select a report date.';
    if (!formData.startTime || !formData.endTime) return 'Please select start and end time.';
    if (!ROLE_OPTIONS.includes(formData.role)) return 'Please select a valid role.';
    const start = combineDateAndTime(formData.reportDate, formData.startTime);
    const end = combineDateAndTime(formData.reportDate, formData.endTime);
    if (!start || !end) return 'Invalid date/time.';
    if (end <= start) return 'End time must be after start time.';
    return '';
  };

  const saveAssignment = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { error(err); return; }
    setSaving(true);
    try {
      const start = combineDateAndTime(formData.reportDate, formData.startTime);
      const end = combineDateAndTime(formData.reportDate, formData.endTime);
      const payload = {
        // Multi-coach support (new format)
        coachIds: formData.coachIds.length > 0 ? formData.coachIds : [],
        coachNames: formData.coachNames.length > 0 ? formData.coachNames : [],
        // Legacy single coach support (for backward compatibility)
        coachId: formData.coachId, coachName: formData.coachName,
        siteId: formData.siteId, siteName: formData.siteName, address: formData.address || '',
        reportDate: formData.reportDate, startTime: formData.startTime, endTime: formData.endTime,
        role: formData.role, notes: formData.notes || '',
        startDateTime: Timestamp.fromDate(start),
        endDateTime: Timestamp.fromDate(end),
        expiresAt: Timestamp.fromDate(addDays(end, 7)), // Updated to 1 week
        updatedAt: serverTimestamp(),
      };
      if (editingAssignment?.id) {
        await updateDoc(doc(db, ASSIGNMENTS_COLLECTION, editingAssignment.id), payload);
        success('Assignment updated successfully!');
      } else {
        await addDoc(collection(db, ASSIGNMENTS_COLLECTION), { ...payload, createdAt: serverTimestamp() });
        success('Assignment created successfully!');
      }
      setIsFormOpen(false);
    } catch (saveErr) {
      error('Failed to save assignment: ' + (saveErr?.message || String(saveErr)));
    } finally {
      setSaving(false);
    }
  };

  const deleteAssignment = async (row) => {
    if (!window.confirm(`Delete assignment for ${row?.coachName || 'this coach'}?`)) return;
    try {
      await deleteDoc(doc(db, ASSIGNMENTS_COLLECTION, row.id));
      success('Assignment deleted successfully!');
    } catch (err) {
      error('Failed to delete assignment: ' + (err?.message || String(err)));
    }
  };

  /* ── Render ── */

  return (
    <div className="w-full">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 bg-white rounded-xl p-4 ">
        <div className='bg-white w-full '>
          <h2 className="text-2xl font-bold text-gray-900">Coach Assignments</h2>
          <p className="text-sm text-black mt-1">
            Manage and schedule coach site assignments. Assignments expire 1
            week after their date.
          </p>
        </div>
        <Button onClick={openNew} className="flex items-center gap-2 shrink-0 my-auto ">
          <Plus size={16} />
          New Assignment
        </Button>
      </div>

      {/* Error */}
      {dataError && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {dataError}
        </div>
      )}

      {/* Controls bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5 bg-white rounded-xl p-4">
        {/* View toggle */}
        <div className="flex items-center gap-1 bg-gray-300 rounded-xl p-1 shrink-0">
          <button
            onClick={() => setActiveView("list")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
              activeView === "list"
                ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <List size={14} /> List view
          </button>
          <button
            onClick={() => setActiveView("location")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
              activeView === "location"
                ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <MapPin size={14} /> Location view
          </button>
          <button
            onClick={() => setActiveView("calendar")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
              activeView === "calendar"
                ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Calendar size={14} /> Calendar view
          </button>
        </div>

        {/* Search */}
        <div className="flex-1 relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder="Search coach, site, role, notes…"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-500 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
          />
        </div>

        {/* Expired controls */}
        <div className="flex items-center gap-2 shrink-0">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-black-900"
            />
            Show expired
          </label>
          <button
            onClick={deleteExpiredAssignments}
            className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-gray-600 border border-gray-200 rounded-lg bg-red-400 hover:bg-red-100 hover:border-red-200 hover:text-red-700 transition-colors"
          >
            <Trash2 size={13} /> Delete expired
          </button>
          {selectedAssignmentIds.length > 0 && (
            <button
              onClick={handleBulkDeleteAssignments}
              className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-white border border-red-200 rounded-lg bg-red-600 hover:bg-red-700 transition-colors"
            >
              <Trash2 size={13} /> Delete Selected ({selectedAssignmentIds.length})
            </button>
          )}
        </div>
      </div>

      {/* ── LIST VIEW ── */}
      {activeView === "list" && (
        <>
          <div className="flex items-center justify-between mb-3 bg-white rounded-xl p-4">
            <div className="flex items-center gap-3">
              {filteredAssignments.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filteredAssignments.every(row => selectedAssignmentIds.includes(row.id))}
                    onChange={() => handleSelectAllOnPage(filteredAssignments.map(row => row.id))}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-[13px] text-gray-600">Select all</span>
                </div>
              )}
              <span className="text-[13px] font-medium text-black-900">All assignments</span>
            </div>
            <span className="text-[12px] text-white bg-blue-500 px-3 py-1 rounded-full">
              {loading ? 'Loading…' : `${filteredAssignments.length} assignment${filteredAssignments.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {loading ? (
              <div className="px-5 py-12 text-center text-sm text-gray-400">
                <div className="animate-pulse">Loading assignments…</div>
              </div>
            ) : filteredAssignments.length === 0 ? (
              <div className="px-5 py-16 text-center">
                <div className="text-gray-300 text-4xl mb-3">📋</div>
                <div className="text-[14px] font-medium text-gray-500">
                  No assignments found
                </div>
                <div className="text-[13px] text-gray-400 mt-1">
                  {searchVal
                    ? "Try a different search term"
                    : "Create your first assignment above"}
                </div>
              </div>
            ) : (
              filteredAssignments.map((row) => (
                <AssignmentListRow
                  key={row.id}
                  row={row}
                  onEdit={openEdit}
                  onDelete={deleteAssignment}
                  isSelected={selectedAssignmentIds.includes(row.id)}
                  onToggleSelection={toggleAssignmentSelection}
                />
              ))
            )}
          </div>
        </>

      )}

      {/* ── LOCATION VIEW ── */}
      {activeView === "location" && (
        <LocationView
          assignments={filteredAssignments}
          pickupSites={pickupSites}
          onEdit={openEdit}
          onDelete={deleteAssignment}
        />
      )}

      {/* ── CALENDAR VIEW ── */}
      {activeView === "calendar" && (
        <WeeklyCalendar
          weekStart={weekStart}
          assignments={weekAssignments}
          onPrevWeek={() => setWeekStart((d) => addDays(d, -7))}
          onNextWeek={() => setWeekStart((d) => addDays(d, 7))}
          onEdit={openEdit}
          onDelete={deleteAssignment}
        />
      )}

      {/* ── FORM MODAL ── */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingAssignment ? "Edit Assignment" : "New Assignment"}
        size="lg"
      >
        <form onSubmit={saveAssignment} className="space-y-5">
          {/* Coaches - Multi-select (Full Width) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Coaches <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {/* Checkbox-based coach selection */}
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-white">
                <div className="space-y-2">
                  {coaches.filter(c => c.approvalStatus === 'approved').map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        value={c.id}
                        checked={formData.coachIds?.includes(String(c.id)) || false}
                        onChange={(e) => {
                          const coachId = String(c.id);
                          const isChecked = e.target.checked;
                          let newCoachIds;
                          
                          if (isChecked) {
                            // Add coach to selection
                            newCoachIds = [...(formData.coachIds || []), coachId];
                          } else {
                            // Remove coach from selection
                            newCoachIds = (formData.coachIds || []).filter(id => id !== coachId);
                          }
                          
                          handleMultiCoachChange(newCoachIds);
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {formatCoachName(c)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Selected coaches display */}
              {(formData.coachIds || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {formData.coachIds.map((coachId, index) => {
                    const coach = coaches.find(c => String(c.id) === String(coachId));
                    return (
                      <span
                        key={coachId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-[11px] rounded-full"
                      >
                        {coach ? formatCoachName(coach) : formData.coachNames?.[index] || 'Unknown'}
                        <button
                          type="button"
                          onClick={() => {
                            const newCoachIds = formData.coachIds.filter(id => id !== coachId);
                            const newCoachNames = newCoachIds.map(id => {
                              const c = coaches.find(coach => String(coach.id) === String(id));
                              return c ? formatCoachName(c) : '';
                            });
                            handleMultiCoachChange(newCoachIds);
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                          title="Remove coach"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-[11px] text-gray-400">
                Click checkboxes to select multiple coaches. Only approved coaches are shown.
              </p>
            </div>
          </div>

          {/* Site — highlighted when editing so the assigned site is easy to spot */}
          <div
            className={
              editingAssignment
                ? "rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3 -m-0.5 shadow-sm"
                : ""
            }
          >
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Site <span className="text-red-500">*</span>
            </label>
            <select
              value={
                formData.siteId === undefined || formData.siteId === null
                  ? ""
                  : String(formData.siteId)
              }
              onChange={(e) => handleSiteChange(e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white transition-all ${
                editingAssignment
                  ? "border-blue-400 font-medium text-gray-900"
                  : "border-gray-200"
              }`}
              required
            >
              <option value="">Select site…</option>
              {formData.siteId !== '' &&
                formData.siteId != null &&
                !pickupSites.some((l) => String(l.id) === String(formData.siteId)) && (
                  <option value={String(formData.siteId)}>
                    {formData.siteName || `Current site (${formData.siteId})`}
                  </option>
                )}
              {pickupSites.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              Pulled from pickup locations (school sites).
            </p>
          </div>

          {/* Address */}
          <TextField
            label="Address"
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            placeholder="Auto-fills from selected site (editable)"
          />

          {/* Date + Times */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomDatePicker
              label="Report Date"
              value={formData.reportDate}
              onChange={(val) => setFormData({ ...formData, reportDate: val })}
              required
              placeholder="mm-dd-yyyy"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) =>
                  setFormData({ ...formData, startTime: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-white transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) =>
                  setFormData({ ...formData, endTime: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-white transition-all"
                required
              />
            </div>
          </div>

          {/* Role + Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-white transition-all"
                required
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {/* Preview card */}
            <div className="flex items-end">
              <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Preview
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-semibold ${roleStyle(formData.role).avatar}`}
                  >
                    {initialsFromName(formData.coachName) || "C"}
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-gray-800">
                      {formData.coachName || "Coach name"}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {formatLongDate(formData.reportDate) || "Date"} ·{" "}
                      {formatTime12h(formData.startTime)} –{" "}
                      {formatTime12h(formData.endTime)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notes
            </label>
            <textarea
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all min-h-[90px] resize-y"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Optional notes…"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              variant="secondary"
              onClick={() => setIsFormOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : editingAssignment
                  ? "Update Assignment"
                  : "Create Assignment"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default CoachAssignments;